import loadable from '@loadable/component';
import { type FunctionComponent, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { configureRoute } from '../../../utils/routes/configure-route';
import { PageLoading } from '../../components/common-components/loading/loading.component';
import { PageContainer } from '../../components/common-components/page/page-container/page-container.component';
import { CornerRibbon } from '../../components/corner-ribbon/corner-ribbon.component';
import { FiveHundred } from '../../components/error-components/500/500.component';
import { FacetDrawer } from '../../components/search-components/facet-drawer/facet-drawer.component';
import { FacetFilterList } from '../../components/search-components/facet-filter-list/facet-filter-list.component';
import { FacetList } from '../../components/search-components/facet-list/facet-list.component';
import { SearchHeader } from '../../components/search-components/search-header/search-header.component';
import { APPENDED_SITE_NAME } from '../../constants/general';
import { COLLECTIONS_ROUTE } from '../../constants/links';
import { AnalyticsHelper } from '../../helpers/analytics/analytics.helper';
import { TrackedEvent, TrackedEventCase } from '../../helpers/analytics/event-types';
import { buildGTMCollectionRedirect } from '../../helpers/analytics/gtm/gtm-utils.helper';
import { buildGTMSearch } from '../../helpers/analytics/gtm/search-event-builders';
import { generatePageViewSearchDataLayer, getTrackingSearchTermFromDropType } from '../../helpers/search-helper/search-analytics.helper';
import { useTrackEvent, useTrackPageView } from '../../hooks/analytics/analytics.hooks';
import { useTrackImpressions } from '../../hooks/analytics/impressions.hooks';
import { useSiteViewPreference } from '../../hooks/apollo/employee/employee.hooks';
import { useGetSearchSource, useProductViewPreference, useSearchResults } from '../../hooks/apollo/search/search.hooks';
import { DyRecommendationContextType, useDyRecommendationContext } from '../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useNavigation } from '../../hooks/navigation/navigation.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { useRouteParams } from '../../hooks/routing/params.hook';
import { type SearchRequestData } from '../../types/search.types';
import { isChromatic } from '../../utils/dom/chromatic.utils';

const LoadableSearchResults = loadable(
	() => import(/* webpackChunkName: "search-results" */ '../../components/search-components/search-results/search-results.component'),
	{
		resolveComponent: ({ SearchResults }) => SearchResults
	}
);

const LoadableSearchFooter = loadable(
	() => import(/* webpackChunkName: "search-footer" */ '../../components/search-components/search-footer/search-footer.component'),
	{
		resolveComponent: ({ SearchFooter }) => SearchFooter
	}
);

const LoadableNoResults = loadable(
	() =>
		import(
			/* webpackChunkName: "no-search-results" */ '../../components/search-components/no-search-results/no-search-results.component'
		),
	{
		resolveComponent: ({ NoResults }) => NoResults
	}
);

const LoadableNoFilteredSearchResults = loadable(
	() =>
		import(
			/* webpackChunkName: "no-filtered-search-results" */ '../../components/search-components/no-filtered-search-results/no-filtered-search-results.component'
		),
	{
		resolveComponent: ({ NoFilteredSearchResults }) => NoFilteredSearchResults
	}
);

const LoadableSolrAlertNotification = loadable(
	() =>
		import(
			/* webpackChunkName: "solr-alert-notification" */ '../../components/search-components/solr-alert-notification/solr-alert-notification.component'
		),
	{
		resolveComponent: ({ SolrAlertNotification }) => SolrAlertNotification
	}
);

export const CollectionsPage: FunctionComponent = () => {
	const { pathname, search } = useRouterLocation();
	const navigate = useNavigation();
	const trackEvent = useTrackEvent();
	const { manufacturerName, collectionName } = useRouteParams<{ collectionName: string; manufacturerName: string }>();
	const collectionTitleSearch = `${decodeURIComponent(String(manufacturerName))} ${decodeURIComponent(String(collectionName))}`;

	const { results, previousResults, page, pageSize, sortBy, loading, error, redirectUrl, request, isNonstock, flagged } =
		useSearchResults();

	const isRedirecting = useRef(false);
	useEffect(() => {
		if (redirectUrl && !isRedirecting.current) {
			isRedirecting.current = true;
			void AnalyticsHelper.track(buildGTMCollectionRedirect(collectionTitleSearch)).finally(() => navigate(redirectUrl, true));
		}
	}, [redirectUrl, collectionTitleSearch, navigate]);

	const searchResults = loading ? (previousResults?.products?.length ? previousResults : null) : results;
	const searchEngine = searchResults?.searchEngine;

	const { employee, showAsEmployee } = useSiteViewPreference();
	const { productViewPreference } = useProductViewPreference();

	const [isFacetDrawerOpen, setFacetDrawerOpen] = useState<boolean>(false);
	const showFacetDrawer = () => setFacetDrawerOpen(true);
	const hideFacetDrawer = () => setFacetDrawerOpen(false);

	useDyRecommendationContext({ type: DyRecommendationContextType.OTHER_PAGE });

	/* track the page view */
	const pageName = 'collection';
	const dropType = 'collection';
	const searchSource = useGetSearchSource();
	const searchDataLayer = generatePageViewSearchDataLayer(
		pageName,
		dropType,
		results,
		page,
		pageSize,
		sortBy,
		collectionTitleSearch,
		searchSource,
		productViewPreference,
		isNonstock
	);
	/* if the page is still loading or if we have a search redirect, don't track this page view */
	const blockTracking = loading || Boolean(redirectUrl);
	useTrackPageView({ pageName, collection: collectionName }, searchDataLayer, blockTracking);
	useTrackImpressions(
		TrackedEvent.COLLECTION_RESULTS_VIEW,
		getTrackingSearchTermFromDropType(dropType),
		request,
		results,
		productViewPreference,
		isNonstock
	);

	// Search tracking.
	const previousRequestRef = useRef<SearchRequestData | null>(null);
	useEffect(() => {
		if (!loading && !error && request !== previousRequestRef.current) {
			const isNewSearch =
				request.manufacturer !== previousRequestRef.current?.manufacturer ||
				request.collectionName !== previousRequestRef.current?.collectionName;
			trackEvent(
				buildGTMSearch(
					TrackedEvent.SEARCH,
					TrackedEventCase.COLLECTION_SEARCH_RESULTS,
					request,
					results,
					productViewPreference,
					employee.isAuthenticated,
					isNonstock,
					isNewSearch,
					undefined,
					flagged
				)
			);

			previousRequestRef.current = request;
		}
	}, [request, loading, error, isNonstock, productViewPreference, results, trackEvent, employee, flagged]);

	const { ref: footerRef, inView: footerInView } = useInView({
		triggerOnce: true
	});

	if (error) {
		return <FiveHundred />;
	}

	const collectionTitle = flagged ? 'Collection Search' : collectionTitleSearch;
	const metaDescription = `Save on products in the ${collectionTitle} collection at Build.com. Low Prices + Fast & Free Shipping on Most Orders. Find reviews, expert advice, manuals, specs & more.`;
	const canonicalURL = flagged ? '' : `${pathname}${search}`;
	const pageTitle = `${collectionTitle}${APPENDED_SITE_NAME}`;

	return (
		<>
			{showAsEmployee && <LoadableSolrAlertNotification searchEngine={searchEngine ?? ''} />}
			<PageContainer
				canonicalURL={canonicalURL}
				metaDescription={metaDescription}
				pageTitle={pageTitle}
				breadcrumbs={(searchResults && searchResults.breadcrumbs) || []}
				showUpdateLocation={true}>
				<>
					{searchResults?.products && searchResults?.breadcrumbs ? (
						searchResults.products.length > 0 || searchResults.selectedFacetGroups.length > 0 ? (
							<>
								<div>
									<SearchHeader title={collectionTitle} onShowDrawer={showFacetDrawer} />
									<div className="dn db-l mt2">
										<FacetFilterList />
									</div>
								</div>
								<div className="flex mt3 mt4-l" data-automation="collection-results-page">
									<div className="w-25-l dn db-l">
										<FacetList />
									</div>
									<div className="w-100 w-75-l h-100 relative">
										<LoadableSearchResults viewType={productViewPreference} dropType={dropType} />
										{searchResults.products.length === 0 ? <LoadableNoFilteredSearchResults /> : null}
										<div ref={footerRef} className="ml3-l">
											{(footerInView || isChromatic()) && <LoadableSearchFooter />}
										</div>
									</div>
								</div>
								{isFacetDrawerOpen && <FacetDrawer onFacetDrawerClose={hideFacetDrawer} />}
								<div id="dy-collection-page-1" />
							</>
						) : (
							<LoadableNoResults type="collection" />
						)
					) : (
						<PageLoading />
					)}
					{showAsEmployee && searchEngine ? <CornerRibbon text={`${searchEngine} Search`} /> : null}
				</>
			</PageContainer>
		</>
	);
};

export const RoutedCollectionsPage = configureRoute({ path: COLLECTIONS_ROUTE }, CollectionsPage);
